import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppService } from '@shared/services/core/app.service';
import { map } from 'rxjs';

import {
  APIResponse,
  ShiftSummaryModel,
  WeekShifts,
  TotalShift,
  ChartData,
  HomeSection,
  QualificationList,
  StaffSummary,
  TimesheetApproval,
  ShiftInvoice,
  ClientList,
  ServiceLocation  
} from '@shared/models/dashboard.interface';
import { Menu } from '@shared/models/sidenav.interface';
import { BusinessError } from '@modules/error/classes';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  clientPortalDomain!: string;
  isAuthorized: boolean = true;

  constructor(private appService: AppService, private http: HttpClient) {
    this.clientPortalDomain = this.appService.getAppConfigs()["CLIENTPORTAL"].BaseUrl;
  }

  formPageRoutes(menuOptions: Array<Menu>): Array<Menu> {
    menuOptions.forEach(option => {
      const currentPageRoute = option.optionKeyword.toLowerCase();
      
      // making CamelCase class names.
      option.cssClass = option.optionKeyword.slice(0, 1).toUpperCase() + option.optionKeyword.toLowerCase().slice(1);
      option.cssClass[0].toUpperCase(); // making the 1st letter uppercase.

      const hasChildren = menuOptions.filter(o => o.parentId && o.parentId === option.id);
      if (hasChildren.length > 0) option.cssClass += ' hasChildren';

      if (option.parentId === 0) {
        option.parentId = undefined;
        option.optionPath = `${currentPageRoute}`
      }
      else {
        const parentOptionIndex = menuOptions.findIndex(potentialParent => potentialParent.id === option.parentId);
        if (parentOptionIndex !== -1) {
          const parentPageRoute = menuOptions[parentOptionIndex].optionKeyword.toLowerCase();
          option.optionPath = `${parentPageRoute}/${currentPageRoute}`
        }
      }
    });

    return menuOptions;
  }

  getMenuListData(clientTypeParameter: string, clientLoginParameter: string, token: string, jti: string, professionalLevel: string = '') {
    const menuListEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetMenuListData"].PATH;
    const url = this.clientPortalDomain + menuListEndpoint;
    
    // Preparing query parameters.
    let queryArgs: string = `ClientType=${clientTypeParameter}&ClientLogin=${clientLoginParameter}`;
    if (professionalLevel) queryArgs +=`&ClientLevel=${professionalLevel}`;

    // converting the menu details response into kendo drawer friendly format.
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'X-Er-Type': `${jti}` });
    return this.http.get<APIResponse<Array<Menu>>>(`${url}?${queryArgs}`, { headers: authHeaders }).pipe(
      map(response => {
        if (response.Data.length > 0) {
          return response.Data;
        } else {
          throw new BusinessError("PORTAL_UNAVAILABLE", "You don’t have access to the portal. Please contact the agency staff");
        }
      }),
      map(menuOptions => this.formPageRoutes(menuOptions))
    );
  }

  getHomeSection() {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetHomeSection"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<HomeSection[]>>(`${url}`);
  }

  getQualifications(clientId: string) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetQualificationsList"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<QualificationList[]>>(`${url}?ClientId=${clientId}`);
  }

  getShiftWeek(requestBody: ShiftSummaryModel) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetShiftWeek"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<WeekShifts[]>>(`${url}?DateFrom=${requestBody.dateFrom}&DateTo=${requestBody.dateTo}&ClientId=${requestBody.clientId}`);
  }

  getShiftCount(requestBody: ShiftSummaryModel) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetShiftCount"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<TotalShift>>(`${url}?DateFrom=${requestBody.dateFrom}&DateTo=${requestBody.dateTo}&ClientId=${requestBody.clientId}`);
  }

  getShiftChart(requestBody: ShiftSummaryModel) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetShiftChart"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<ChartData[]>>(`${url}?DateFrom=${requestBody.dateFrom}&DateTo=${requestBody.dateTo}&ClientId=${requestBody.clientId}`);
  }

  getStaffSummary(clientId: string) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetStaffSummary"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<StaffSummary[]>>(`${url}?ClientId=${clientId}`);
  }

  getAgencyFillRatio(requestBody: ShiftSummaryModel) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetAgencyFillRatio"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<WeekShifts[]>>(`${url}?DateFrom=${requestBody.dateFrom}&DateTo=${requestBody.dateTo}&ClientId=${requestBody.clientId}`);
  }

  getTimesheetApproval(requestBody: ShiftSummaryModel) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetTimesheetApproval"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<TimesheetApproval[]>>(`${url}?DateFrom=${requestBody.dateFrom}&DateTo=${requestBody.dateTo}&ClientId=${requestBody.clientId}`);
  }

  getShiftsInvoice(requestBody: ShiftSummaryModel) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetShiftsInvoice"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<ShiftInvoice[]>>(`${url}?DateFrom=${requestBody.dateFrom}&DateTo=${requestBody.dateTo}&ClientId=${requestBody.clientId}`);
  }

  getShiftsUnInvoice(requestBody: ShiftSummaryModel) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetShiftsUnInvoice"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<ShiftInvoice[]>>(`${url}?DateFrom=${requestBody.dateFrom}&DateTo=${requestBody.dateTo}&ClientId=${requestBody.clientId}`);
  }

  getClientList(clientId: string) {
    const userAuthAPIEndpoint = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetClientList"].PATH;
    const url = this.clientPortalDomain + userAuthAPIEndpoint;
    return this.http.get<APIResponse<ClientList[]>>(`${url}?ProfessionalId=${clientId}`);
  }

  getServiceLocation(ClientId: string, ClientType: string) {
    const serviceLocationAPI = this.appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["BOOKING"]["GetServiceLocation"].PATH;
    const url = this.clientPortalDomain + serviceLocationAPI;
    return this.http.get<APIResponse<ServiceLocation[]>>(`${url}?ClientId=${ClientId}&ClientType=${ClientType}`);
  }
}